import { SortPayload } from 'react-admin';
import { GetListParams } from '@/features/common/common.types';
import { User } from '@/features/users/users.types';
import { LastMessage } from '@/features/messages/messages.types';
import { Message, TimelineItem } from '@/features/messages/messages.types';
import z from 'zod';

// Shared types for conversation container components
export interface ConversationUser {
  id?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  avatar?: string;
  profile_image?: string;
}

export interface ConversationAgent {
  id: string;
  name: string;
  avatar: string;
}

export interface ExtendedTimelineItem extends TimelineItem {
  _status?: Message['_status'];
  _animateDelivery?: boolean;
}

export interface ExtendedMessage extends Message {
  _isRetry?: boolean;
  _realId?: string;
}

export interface SenderDetails {
  isUser: boolean;
  avatar: string;
  name: string;
  isAIAgent: boolean;
}

export interface DateGroup {
  date: string;
  items: ExtendedTimelineItem[];
}

export type AgentType = 'ai' | 'human';
export type AgentStatus = 'active' | 'inactive' | 'away';
export type MessageStatus = 'pending' | 'sent' | 'delivered' | 'error';

export interface IConversationsResponse {
  data: IConversation[];
  total: number;
  pageInfo: IPageInfo;
}

export interface IConversation {
  id: string;
  chatbot_id: string;
  summary: string;
  last_message_at: string;
  status: string;
  has_agent_active: boolean;
  start_date: string;
  end_date: string;
  is_read: boolean;
  has_attachments: boolean;
  user_id: string;
  location_id: string;
  organisation_id: string;
  wab_chatId_external: string;
  realtime: boolean;
  channel_id: string;
  channel_name: string;
  user: User;
  lastMessage: LastMessage;
  unreadCount: number;
}

export interface IPageInfo {
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  totalPages: number;
  totalItems: number;
  currentPage: number;
}

export type GetConversationsParams = GetListParams;

// Define types for our conversations
export interface Conversation {
  id: string;
  summary: string;
  status: string;
  has_agent_active: boolean;
  is_read: boolean;
  has_attachments: boolean;
  realtime: boolean;
  last_message_at: string;
  start_date: string;
  end_date: string;
  chatbot_id: string;
  user_id: string;
  location_id: string;
  organisation_id: string;
  wab_chatId_external?: string;
  // Add channel information
  channel_id?: string;
  channel_name?: string;
  // Related data that we'll populate
  user?: User;
  lastMessage?: LastMessage;
  unreadCount?: number;
}

// Filter options - using explicit boolean types to handle undefined (no filter)
export interface ConversationFilters {
  status?: string;
  is_read?: boolean; // true, false, or undefined
  has_agent_active?: boolean; // true, false, or undefined
  has_attachments?: boolean; // true, false, or undefined
  isMember?: boolean; // true, false, or undefined
  searchTerm?: string;
}

// Interface for conversation handover data
export interface ConversationHandoverData {
  conversation_id: string;
  from_user_id?: string | null; // User performing the handover
  to_user_id?: string | null; // User receiving the handover (if to another user)
  agent_id?: string | null; // The AI agent ID when handing over to AI
  handover_reason?: string;
  is_agent_handover: boolean; // true if handover to AI, false if handover to human
  handover_status?: string;
}

export interface UseConversationsProps {
  perPage?: number;
  initialSort?: SortPayload;
  initialFilter?: ConversationFilters;
}

export interface UseConversationsReturn {
  conversations: Conversation[];
  total: number;
  loading: boolean;
  error: Error | null;
  page: number;
  perPage: number;
  setPage: (page: number) => void;
  sort: SortPayload;
  setSort: (sort: SortPayload) => void;
  filters: ConversationFilters;
  setFilters: (filters: Partial<ConversationFilters>) => void;
  selectedConversationId?: string | null | undefined;
  setSelectedConversationId: (id: string | null) => void;
  selectedConversation: Conversation | null | undefined;
  refreshConversations: () => void;
  updateAgentStatus: (
    conversationId: string,
    hasAgentActive: boolean,
    handoverData: Partial<ConversationHandoverData>,
  ) => Promise<boolean>;
  createConversation: (data: {
    channel_id: string;
    phone_number: string;
    summary: string;
    has_agent_active: boolean;
    wab_chatId_external?: string;
  }) => Promise<Conversation | null>;
  isUpdatingAgentStatusPending: boolean;
}

export type CreateConversationBody = {
  summary: string;
  status: string;
  has_agent_active: boolean;
  is_read: boolean;
  has_attachments: boolean;
  realtime: boolean;
  chatbot_id: string;
  user_id: string;
  organisation_id: string;
  location_id: string;
  wab_chatId_external: string;
  last_message_at: string;
  start_date: string;
  end_date: string | null;
};

export type CreateConversationChannelBody = {
  conversation_id: string;
  channel_id: string;
};

export type CreateConversationHandoverBody = {
  id: string;
  conversation_id: string;
  from_user_id?: string | null;
  to_user_id?: string | null;
  agent_id?: string | null;
  handover_reason: string;
  is_agent_handover: boolean;
  handover_status: string;
};

export type UpdateConversationBody = Partial<Conversation>;

export interface ConversationData {
  channel_id: string;
  phone_number: string;
  summary: string;
  has_agent_active: boolean;
  wab_chatId_external?: string;
  chatbot_id?: string;
  user_id?: string;
}

// Zod validation schema
export const conversationSchema = z.object({
  channelId: z.string().min(1, 'Please select a WhatsApp channel'),
  chatbotId: z.string().optional(),
  phoneNumber: z
    .string()
    .min(1, 'Phone number is required')
    .regex(
      /^\+?\d{10,15}$/,
      'Please enter a valid phone number (10-15 digits)',
    ),
  userFirstName: z.string(),
  userLastName: z.string(),
  userMember: z.boolean().optional(),
  initialMessage: z.string().optional(),
});

export type ConversationFormData = z.infer<typeof conversationSchema>;

export type DefaultConversationFormData = Pick<
  ConversationFormData,
  'userFirstName' | 'userLastName' | 'userMember' | 'phoneNumber'
>;
